import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const buttonVariants = cva(
  'font-untitled inline-flex items-center justify-center gap-[10px] whitespace-nowrap rounded-[8px] text-[14px] font-medium leading-[1.2] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        happy: 'bg-ui-green/30 text-white shadow border-ui-green border border-solid',
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border-2 border-primary text-primary hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary rounded-none underline-offset-4 hover:underline',
        input:
          'border border-input bg-gray-50/24 text-gray-900 text-base md:text-sm font-normal shadow-sm',
        grayText: 'text-gray-500',
        cta: 'text-black bg-jh-yellow border-2 border-black hover:bg-jh-yellow/70',
        hightowerOutline:
          'border border-input bg-card text-jh-green hover:bg-muted focus-visible:ring-2 focus-visible:ring-jh-green/25 [&_svg]:size-5',
      },
      size: {
        clear: '',
        default: 'h-[28px] min-w-[81px] px-4 py-2',
        sm: 'h-[28px] min-w-[81px] px-4 py-2',
        lg: 'h-10 px-8',
        xl: 'h-14 px-6 text-base md:text-lg md:h-16 md:px-10',
        icon: 'h-9 w-9',
        hightower: 'h-[28px] min-w-[81px] px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
